var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useUnmount } from 'react-use';
import { useS3FileUpload } from 'app/domains/filesystem';
import { useAutoTagDocuments } from 'app/domains/metadata';
import { useAddDocumentsToProject } from 'app/domains/projects';
import useAccount from 'shared/domains/users/hooks/useAccount';
import { bulkUploadMutation } from './mutation';
import { usePollUploadJob } from './usePollUploadJob';
import { transformResultsToInputs } from './utils';
import { useUpload } from '../UploadProvider';
export const useUploadDocuments = (callback) => {
    const isUnmountedRef = useRef(false);
    const [addDocumentsToProject] = useAddDocumentsToProject();
    const autoTagJob = useAutoTagDocuments();
    const { autoTag, selectedProjects } = useUpload();
    const { data: accountData } = useAccount();
    const { companyId } = accountData;
    const { upload } = useS3FileUpload();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const { pollUploadJob } = usePollUploadJob((_a) => __awaiter(void 0, [_a], void 0, function* ({ status, entries = [] }) {
        const entryIds = entries.map((entry) => entry.id);
        const extraJobs = [];
        if (status === 'success' && autoTag && entryIds.length > 0) {
            extraJobs.push(autoTagJob.mutate(entryIds));
        }
        if (status === 'success' && entryIds.length > 0 && selectedProjects.length > 0) {
            extraJobs.push(...selectedProjects.map((project) => addDocumentsToProject(project.id, entryIds.map((id) => ({
                documentId: id,
                aclActions: project.aclActions,
            })))));
        }
        if (extraJobs.length > 0) {
            yield Promise.all(extraJobs);
        }
        if (status === 'success') {
            callback({ type: 'success', entryIds });
        }
        if (status === 'error') {
            callback({ type: 'error', entryIds: [] });
        }
        setLoading(false);
    }));
    const [handler] = useMutation(bulkUploadMutation);
    const handleProgress = (progress) => setUploadProgress(progress);
    const handleError = () => {
        callback({ type: 'error', entryIds: [] });
        setUploading(false);
        setLoading(false);
    };
    const uploadFolder = (files, parentId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            setUploading(true);
            const results = yield upload(files, handleProgress);
            setUploading(false);
            const uploads = transformResultsToInputs(results);
            const bundleVersion = process.env.PLEDITOR_BUNDLE_VERSION;
            const input = { companyId, parentId, uploads, bundleVersion };
            // If the user cancels the upload during the s3 request we don't want to continue
            if (isUnmountedRef.current) {
                return;
            }
            const { data } = yield handler({ variables: { input } });
            const jobId = data === null || data === void 0 ? void 0 : data.uploadFileSystemDocumentsAsync.id;
            if (jobId) {
                pollUploadJob(jobId);
            }
            else {
                handleError();
            }
        }
        catch (_b) {
            handleError();
        }
    });
    useUnmount(() => {
        isUnmountedRef.current = true;
    });
    return { uploadFolder, loading, uploading, uploadProgress };
};
